import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Tooltip, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { RootState } from 'store/configureStore';
import CustomTable from 'components/Tables/CustomTable';
import InternalControlLayout from 'components/Layouts/InternalControlLayout';
import { getControlColumns } from 'constants/controlColumns';
import ApiService from 'utils/ApiCalls';
import {
  ControlRow,
  QueryScope,
  DropdownItem
} from 'store/ducks/InternalControls/types';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { internalControlsOp } from 'store/ducks/InternalControls';
import qs from 'qs';
import { useLocation } from 'react-router-dom';
import { ArrayParam, useQueryParams, withDefault } from 'use-query-params';
import dayjs from 'dayjs';
import * as Styled from '../RiskControl.styled';
import * as ControlStyled from '../InternalControls/InternalControls.styled';

const Controls: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { drawTable, selectedEntityId, lang } = useSelector(
    (state: RootState) => ({
      drawTable: state.internalControls.drawTable,
      selectedEntityId: state.internalControls.selectedEntityId,
      lang: state.global.lang
    })
  );

  // Sync language with i18n
  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  const [controls, setControls] = useState<ControlRow[]>([]);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const originalControlsRef = useRef<ControlRow[]>([]);
  const [newControlIds, setNewControlIds] = useState<Set<number>>(new Set());
  const [isDirty, setIsDirty] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const dispatch = useDispatch();

  // Simple tracking function that marks data as dirty
  const trackingSetControls = (
    newControls: ControlRow[] | ((prev: ControlRow[]) => ControlRow[])
  ) => {
    if (typeof newControls === 'function') {
      setControls((prev) => {
        const result = newControls(prev);
        setIsDirty(true);
        return result;
      });
    } else {
      setControls(newControls);
      setIsDirty(true);
    }
  };

  // Global error handler to catch any unhandled errors
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      const errorInfo = {
        timestamp: new Date().toISOString(),
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack || 'No stack trace',
        type: 'window.onerror'
      };
      localStorage.setItem('controlsGlobalError', JSON.stringify(errorInfo));
      event.preventDefault();
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const errorInfo = {
        timestamp: new Date().toISOString(),
        reason: event.reason,
        type: 'unhandledrejection'
      };
      localStorage.setItem('controlsGlobalError', JSON.stringify(errorInfo));
      event.preventDefault();
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, []);

  useEffect(() => {
    setSaveDisabled(!isDirty && newControlIds.size === 0);
    dispatch(
      internalControlsOp.setHasChanges(isDirty || newControlIds.size > 0)
    );
  }, [isDirty, newControlIds, dispatch]);

  const [owner, setOwner] = useState<{ label: string; value: number }[]>([]);
  const [controlNature, setControlNature] = useState<
    { label: string; value: number }[]
  >([]);
  const [controlFrequency, setControlFrequency] = useState<
    { label: string; value: number }[]
  >([]);
  const [controlPerformer, setControlPerformer] = useState<
    { label: string; value: number }[]
  >([]);
  const [controlStatus, setControlStatus] = useState<
    { label: string; value: number }[]
  >([]);
  const [controlOrigin, setControlOrigin] = useState<
    { label: string; value: number }[]
  >([]);
  const [controlCustodian, setControlCustodian] = useState<
    { label: string; value: number }[]
  >([]);
  const [controlTypes, setControlTypes] = useState<
    { label: string; value: number }[]
  >([]);
  const [reasonForControlStatus, setReasonForControlStatus] = useState<
    { label: string; value: number }[]
  >([]);
  const [controlPrerequisites, setControlPrerequisites] = useState<any[]>([]);
  const location = useLocation();

  const queryParams = qs.parse(location.search, {
    ignoreQueryPrefix: true
  });

  const FiltersParam = withDefault(ArrayParam, []);

  const [query, setQuery] = useQueryParams({
    group: FiltersParam,
    division: FiltersParam,
    region: FiltersParam,
    legal_entity: FiltersParam,
    business_unit: FiltersParam,
    control_owner: FiltersParam,
    main_organization: FiltersParam
  });

  const getValidIdFromArray = (value: unknown): number | undefined => {
    if (!Array.isArray(value) || value.length === 0) return undefined;

    const num = Number(value[0]);
    return Number.isInteger(num) && num > 0 ? num : undefined;
  };

  const buildSingleControlParamFromQuery = () => {
    const groupId = getValidIdFromArray(query.group);
    if (groupId) return { group_id: groupId };

    const divisionId = getValidIdFromArray(query.division);
    if (divisionId) return { division_id: divisionId };

    const regionId = getValidIdFromArray(query.region);
    if (regionId) return { region_id: regionId };

    const legalEntityId = getValidIdFromArray(query.legal_entity);
    if (legalEntityId) return { legal_entity_id: legalEntityId };

    const businessUnitId = getValidIdFromArray(query.business_unit);
    if (businessUnitId) return { business_unit_id: businessUnitId };

    const mainOrganizationId = getValidIdFromArray(query.main_organization);
    if (mainOrganizationId) return { main_organization_id: mainOrganizationId };
    return {};
  };

  const isScopedView = useMemo(() => {
    const scope = buildSingleControlParamFromQuery();
    return Object.keys(scope).length > 0;
  }, [query]);

  const hasMainOrganization =
    query.main_organization &&
    Array.isArray(query.main_organization) &&
    query.main_organization.length > 0;

  useEffect(() => {
    const isQueryCompletelyEmpty = (q: any) => {
      return Object.values(q || {}).every(
        (val) => Array.isArray(val) && val.length === 0
      );
    };

    const fetchControls = async () => {
      try {
        const isEmptyQuery = isQueryCompletelyEmpty(query);

        if (isEmptyQuery) {
          setControls([]);
          originalControlsRef.current = [];
          return;
        }

        let response;

        if (hasMainOrganization) {
          const mainOrganizationIds = (query.main_organization ?? [])
            .filter((v): v is string => v !== null)
            .map(Number)
            .filter(Boolean);

          response = await ApiService.getOrgControl({
            main_organization_id: mainOrganizationIds
          });
        } else {
          response = await ApiService.getControlsBPR(
            buildSingleControlParamFromQuery()
          );
        }
        const apiControls = response.data.data;

        const mappedControls = apiControls.map((item: any) => {
          const instance = item.instances?.[0];

          return {
            key: item.id.toString(),
            id: item.id,

            controlName: item.control_name,
            controlDescription: item.control_description,
            controlObjective: item.control_objective,
            keyControl: item.key_control || false,
            linkedRisks: item.linked_risk_maintenance ?? [],
            notUsed: item.not_used === true,

            controlType: {
              id: item.control_type?.id ?? null,
              name: item.control_type?.name ?? null
            },

            controlNature: {
              id: item.control_nature?.id ?? null,
              name: item.control_nature?.name ?? null
            },

            controlFrequency: {
              id: item.control_frequency?.id ?? null,
              name: item.control_frequency?.name ?? null
            },

            testingMethod: item.testing_method,

            startDate: item.start_date,
            endDate: item.end_date || '9999-01-01',

            controlOrigin: {
              id: item.control_origin?.id ?? null,
              name: item.control_origin?.name ?? null
            },

            controlCustodian: {
              id: item.control_custodian?.id ?? null,
              name: item.control_custodian?.name ?? ''
            },

            active: item.active,
            version: item.version,
            comments: item.comments,

            instance_id: instance?.instance_id ?? null,
            monitoringlevel: instance?.monitoring_level ?? null,

            controlPerformer: {
              id: instance?.control_performer?.id ?? null,
              name: instance?.control_performer?.name ?? null
            },

            controlStatus: {
              id: instance?.control_status?.id ?? null,
              name: instance?.control_status?.name ?? null
            },

            owner: {
              id: instance?.owner?.id ?? null,
              name: instance?.owner?.name ?? null
            },

            reasonForControlStatus: {
              id: instance?.reason_for_control_status?.id ?? null,
              name: instance?.reason_for_control_status?.name ?? ''
            },

            controlPrerequisites: {
              id: instance?.control_prerequisites?.id ?? null,
              name: instance?.control_prerequisites?.name ?? ''
            },

            controlOverallEffectiveness: {
              id: instance?.control_overall_effectiveness?.id ?? null,
              name: instance?.control_overall_effectiveness?.name ?? null
            }
          };
        });

        setControls(mappedControls);
        originalControlsRef.current = JSON.parse(
          JSON.stringify(mappedControls)
        );
        setIsDirty(false);

        dispatch(internalControlsOp.setDrawTable(false));
      } catch (error) {
        console.error('Error fetching controls:', error);
      }
    };

    fetchControls();
  }, [
    drawTable,
    dispatch,
    queryParams.control_owner,
    isScopedView,
    query.main_organization?.join(',')
  ]);

  useEffect(() => {
    const fetchAllDropdowns = async () => {
      try {
        const [
          ownerRes,
          controlTypesResponse,
          controlNatureRes,
          controlFrequencyRes,
          controlPerformerRes,
          controlStatusRes,
          controlOriginRes,
          controlCustodianRes,
          reasonForControlStatusRes,
          controlPrerequisitesRes
        ] = await Promise.all([
          ApiService.getOwnersBPR(),
          ApiService.getControlTypesBPR(),
          ApiService.getControlNatureBPR(),
          ApiService.getControlFrequencyBPR(),
          ApiService.getControlPerformerBPR(),
          ApiService.getControlStatusBPR(),
          ApiService.getControlOriginBPR(),
          ApiService.getControlCustodianBPR(),
          ApiService.getReasonsForControlStatusBPR(),
          ApiService.getControlPrerequisites()
        ]);

        setOwner(
          [...ownerRes.data.data]
            .sort((a: DropdownItem, b: DropdownItem) =>
              a.name.localeCompare(b.name)
            )
            .map((o: DropdownItem) => ({
              label: o.name,
              value: o.id
            }))
        );

        setControlTypes(
          [...controlTypesResponse.data.data]
            .sort((a: DropdownItem, b: DropdownItem) =>
              a.name.localeCompare(b.name)
            )
            .map((type: DropdownItem) => ({
              label: type.name,
              value: type.id
            }))
        );

        setControlNature(
          [...controlNatureRes.data.data]
            .sort((a: DropdownItem, b: DropdownItem) =>
              a.name.localeCompare(b.name)
            )
            .map((o: DropdownItem) => ({
              label: o.name,
              value: o.id
            }))
        );

        setControlFrequency(
          [...controlFrequencyRes.data.data]
            .sort((a: DropdownItem, b: DropdownItem) =>
              a.name.localeCompare(b.name)
            )
            .map((o: DropdownItem) => ({
              label: o.name,
              value: o.id
            }))
        );

        setControlPerformer(
          [...controlPerformerRes.data.data]
            .sort((a: DropdownItem, b: DropdownItem) =>
              a.name.localeCompare(b.name)
            )
            .map((o: DropdownItem) => ({
              label: o.name,
              value: o.id
            }))
        );

        setControlStatus(
          [...controlStatusRes.data.data]
            .sort((a: DropdownItem, b: DropdownItem) =>
              a.name.localeCompare(b.name)
            )
            .map((o: DropdownItem) => ({
              label: o.name,
              value: o.id
            }))
        );
        setControlOrigin(
          [...controlOriginRes.data.data]
            .sort((a: DropdownItem, b: DropdownItem) =>
              a.name.localeCompare(b.name)
            )
            .map((o: DropdownItem) => ({
              label: o.name,
              value: o.id
            }))
        );
        setControlCustodian(
          [...controlCustodianRes.data.data]
            .sort((a: DropdownItem, b: DropdownItem) =>
              a.name.localeCompare(b.name)
            )
            .map((o: DropdownItem) => ({
              label: o.name,
              value: o.id
            }))
        );
        setReasonForControlStatus(
          [...reasonForControlStatusRes.data.data]
            .sort((a: DropdownItem, b: DropdownItem) =>
              a.name.localeCompare(b.name)
            )
            .map((o: DropdownItem) => ({
              label: o.name,
              value: o.id
            }))
        );

        setControlPrerequisites(
          [...controlPrerequisitesRes.data.data]
            .sort((a: DropdownItem, b: DropdownItem) =>
              a.name.localeCompare(b.name)
            )
            .map((o: DropdownItem) => ({
              label: o.name,
              value: o.id
            }))
        );
      } catch (err) {
        console.error('Failed to fetch dropdown data', err);
      }
    };

    fetchAllDropdowns();
  }, []);

  const buildNormalizedScope = (): QueryScope => {
    const singleScope = buildSingleControlParamFromQuery();
    return {
      group_id: singleScope.group_id ?? null,
      division_id: singleScope.division_id ?? null,
      region_id: singleScope.region_id ?? null,
      legal_entity_id: singleScope.legal_entity_id ?? null,
      business_unit_id: singleScope.business_unit_id ?? null,
      main_organization_id: singleScope.main_organization_id ?? null
    };
  };

  const controlColumns = useMemo(
    () =>
      getControlColumns(
        owner,
        controlTypes,
        controls,
        controlNature,
        controlFrequency,
        controlPerformer,
        controlStatus,
        controlOrigin,
        controlCustodian,
        reasonForControlStatus,
        trackingSetControls,
        newControlIds,
        hasMainOrganization,
        controlPrerequisites,
        t
      ),
    [
      owner,
      controlTypes,
      controls,
      controlNature,
      controlFrequency,
      controlPerformer,
      controlStatus,
      controlOrigin,
      controlCustodian,
      reasonForControlStatus,
      newControlIds,
      controlPrerequisites,
      hasMainOrganization,
      t
    ]
  );

  const handleAddControlRow = () => {
    const newId = generateUniqueId();
    const newRow: any = {
      key: (controls.length + 1).toString(),
      id: newId,
      instance_id: 0,
      controlName: '',
      monitoringlevel: '',
      controlDescription: '',
      controlObjective: '',
      controlType: '',
      controlNature: '',
      controlFrequency: '',
      owner: '',
      controlPerformer: '',
      testingMethod: '',
      endDate: '9999-01-01',
      startDate: null,
      controlStatus: '',
      controlOrigin: '',
      controlCustodian: '',
      reasonForControlStatus: ''
    };

    trackingSetControls((prev: ControlRow[]) => [...prev, newRow]);
    setNewControlIds((prev: Set<number>) => new Set([...prev, newId]));
  };

  const preparePayload = (controlsPerRow: ControlRow[], scope: QueryScope) => {
    const hasScope = Object.values(scope).some(
      (value) => value !== null && value !== undefined
    );

    return {
      data_list: controlsPerRow.map((ctrl) => {
        const basePayload = {
          id: ctrl.id,
          control_name: ctrl.controlName || null,
          control_description: ctrl.controlDescription || null,
          key_control: ctrl.keyControl || false,
          control_objective: ctrl.controlObjective || null,
          control_type: ctrl.controlType?.id ?? null,
          control_nature: ctrl.controlNature?.id ?? null,
          control_frequency: ctrl.controlFrequency?.id ?? null,
          testing_method: ctrl.testingMethod?.id ?? null,
          test_type: null,
          control_origin: ctrl.controlOrigin?.id ?? null,
          control_custodian: ctrl.controlCustodian?.id ?? null,
          start_date: ctrl.startDate
            ? dayjs(ctrl.startDate).format('YYYY-MM-DD')
            : null,
          end_date: ctrl.endDate
            ? dayjs(ctrl.endDate).format('YYYY-MM-DD')
            : null,
          deleted: false,
          ...(scope.main_organization_id && {
            main_organization_id: scope.main_organization_id
          })
        };

        if (hasMainOrganization) {
          return basePayload;
        }

        return {
          ...basePayload,
          instance: {
            group: scope.group_id ?? null,
            division: scope.division_id ?? null,
            region: scope.region_id ?? null,
            legal_entity: scope.legal_entity_id ?? null,
            business_unit: scope.business_unit_id ?? null,
            owner: ctrl.owner?.id ?? null,
            control_performer: ctrl.controlPerformer?.id ?? null,
            control_status: ctrl.controlStatus?.id ?? null,
            reason_for_control_status: ctrl.reasonForControlStatus?.id ?? null,
            control_prerequisites: ctrl.controlPrerequisites?.id ?? null,
            control_overall_effectiveness_id:
              ctrl.controlOverallEffectiveness?.id ?? null,
            monitoring_level: ctrl.monitoringlevel || null,
            assertion_area_1_id: 4
          }
        };
      })
    };
  };

  const handleControlSave = async () => {
    try {
      if (!isDirty && newControlIds.size === 0) {
        notification.info({
          message: 'No Changes',
          description: 'No changes detected to save.',
          placement: 'topRight',
          duration: 3
        });
        return;
      }

      // Find changed controls by comparing with original
      const changedControls = controls.filter((control) => {
        if (newControlIds.has(control.id)) return true;
        const original = originalControlsRef.current.find(
          (c) => c.id === control.id
        );
        if (!original) return false;

        // Check if control has actually changed
        return JSON.stringify(control) !== JSON.stringify(original);
      });

      if (changedControls.length === 0) {
        notification.info({
          message: 'No Changes',
          description: 'No changes detected to save.',
          placement: 'topRight',
          duration: 3
        });
        return;
      }

      const scope = buildNormalizedScope();
      const payload = preparePayload(changedControls, scope);
      await ApiService.saveControlsBPR(payload);

      const clearedControls = controls.map(
        ({ _pinned, ...rest }) => rest as ControlRow
      );
      setControls(clearedControls);
      originalControlsRef.current = JSON.parse(JSON.stringify(clearedControls));
      setNewControlIds(new Set());
      setIsDirty(false);
      dispatch(internalControlsOp.setHasChanges(false));

      notification.success({
        message: 'Controls Saved',
        description: `${changedControls.length} control(s) saved successfully.`,
        placement: 'topRight',
        duration: 3
      });
    } catch (error: any) {
      localStorage.setItem(
        'controlsSaveError',
        JSON.stringify({
          timestamp: new Date().toISOString(),
          message: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
          stack: error?.stack
        })
      );

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Unknown error occurred';
      const statusCode = error?.response?.status || 'Unknown';

      notification.error({
        message: `Save Failed (${statusCode})`,
        description: errorMessage,
        placement: 'topRight',
        duration: 10
      });

      return false;
    }
  };

  return (
    <InternalControlLayout onSave={handleControlSave}>
      <Styled.ControlContentWrapper>
        <Styled.GlobalTooltipStyles />
        <style>{ControlStyled.getControlValidityFilterStyle()}</style>
        <CustomTable
          drawTable={true}
          rowKey='key'
          columns={controlColumns}
          dataSource={controls}
          itemsCount={controls.length}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: controls.length,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number, range: [number, number]) =>
              `${range[0]}-${range[1]} of ${total} items`,
            onChange: (page: number, size: number) => {
              setCurrentPage(page);
              if (size !== pageSize) {
                setPageSize(size);
              }
            }
          }}
          rowClassName={(record: { notUsed: boolean }) =>
            record.notUsed ? 'disabled-row has-row-tooltip' : ''
          }
          onRow={(record: { notUsed: boolean }) => {
            if (!record.notUsed) return {};

            return {
              'data-tooltip':
                'Disabled due to a previous Risk/Control type change.'
            };
          }}
          scroll={{ x: 2500 }}
        />
        <Styled.AddRowArea>
          <Tooltip
            title={
              !hasMainOrganization
                ? 'It is possible to add a control only if Main Organization is selected.'
                : ''
            }
          >
            <Button
              type='primary'
              onClick={handleAddControlRow}
              disabled={!hasMainOrganization}
            >
              Add new control
            </Button>
          </Tooltip>
        </Styled.AddRowArea>
      </Styled.ControlContentWrapper>
    </InternalControlLayout>
  );
};

export default Controls;
